/* v2.js — motion + video interactions for the v2 site
   All behavior scoped so it plays nice with v1 sections still on the page.
*/
(function () {
  'use strict';

  // ─────────────────────────────────────────────
  // 0a. Word-splitting for .v2-reveal-words — wraps each word in a span
  // so CSS can slide them up individually on reveal.
  // ─────────────────────────────────────────────
  function splitWords(el) {
    if (el.dataset.v2Split === '1') return;
    el.dataset.v2Split = '1';
    var walkAndWrap = function (node) {
      if (node.nodeType === 3 /* text */) {
        var text = node.textContent;
        if (!text.trim()) return;
        var frag = document.createDocumentFragment();
        var words = text.split(/(\s+)/);
        var i = 0;
        words.forEach(function (w) {
          if (/^\s+$/.test(w)) {
            frag.appendChild(document.createTextNode(w));
          } else if (w) {
            var wrap = document.createElement('span');
            wrap.className = 'v2-word';
            var inner = document.createElement('span');
            inner.textContent = w;
            inner.setAttribute('data-i', String(Math.min(i, 8)));
            wrap.appendChild(inner);
            frag.appendChild(wrap);
            i++;
          }
        });
        node.parentNode.replaceChild(frag, node);
      } else if (node.nodeType === 1 /* element */) {
        // Recurse — clone children list first because we mutate during iteration
        var kids = Array.prototype.slice.call(node.childNodes);
        kids.forEach(walkAndWrap);
      }
    };
    walkAndWrap(el);
  }
  document.querySelectorAll('.v2-reveal-words').forEach(splitWords);

  // ─────────────────────────────────────────────
  // 0b. Number counter for [data-counter="123"] elements
  //     Counts from 0 to target over ~1.6s on first view.
  // ─────────────────────────────────────────────
  function animateCounter(el) {
    if (el.dataset.v2Counted === '1') return;
    el.dataset.v2Counted = '1';
    var raw = el.getAttribute('data-counter') || '';
    var target = parseFloat(raw.replace(/[^\d.-]/g, '')) || 0;
    var suffix = raw.replace(/^[\d.-]+/, '') || ''; // e.g. "M+", "h", "K"
    var duration = 1600;
    var start = null;
    function frame(ts) {
      if (start === null) start = ts;
      var t = Math.min(1, (ts - start) / duration);
      var eased = 1 - Math.pow(1 - t, 3);
      var current = target * eased;
      var displayNum;
      if (target >= 100)      displayNum = Math.round(current);
      else if (target >= 10)  displayNum = current.toFixed(0);
      else                    displayNum = current.toFixed(0);
      el.textContent = displayNum + suffix;
      if (t < 1) requestAnimationFrame(frame);
    }
    requestAnimationFrame(frame);
  }

  // ─────────────────────────────────────────────
  // 1. Reveal-on-scroll for .v2-rise / .v2-reveal-words / [data-counter]
  // ─────────────────────────────────────────────
  if (typeof IntersectionObserver !== 'undefined') {
    var revealTargets = document.querySelectorAll('.v2-rise, .v2-reveal-words, [data-counter]');
    if (revealTargets.length) {
      var revealer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            if (entry.target.hasAttribute('data-counter')) {
              animateCounter(entry.target);
            }
            revealer.unobserve(entry.target);
          }
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });

      revealTargets.forEach(function (el) { revealer.observe(el); });
    }
  } else {
    document.querySelectorAll('.v2-rise, .v2-reveal-words').forEach(function (el) {
      el.classList.add('is-visible');
    });
    document.querySelectorAll('[data-counter]').forEach(animateCounter);
  }

  // ─────────────────────────────────────────────
  // 2. Lazy video loading + play/pause based on visibility.
  //
  //    Videos ship with no src at all — just a poster plus data-v2-sd (480p)
  //    and data-v2-hd (720p). A src is attached only when the tile nears the
  //    viewport. Previously every clip carried its 4K original in the markup
  //    with `autoplay`, so the homepage kicked off ~470MB across 18 elements
  //    on load; on mobile that is what made them crawl.
  // ─────────────────────────────────────────────
  var lazyVideos = document.querySelectorAll('video[data-v2-sd], video[data-v2-hd]');

  // Phones and tablets always get 480p. Above that, pick by how many device
  // pixels the tile actually occupies — a 360px grid cell never needs 720p.
  function pickSource(v) {
    var sdUrl = v.getAttribute('data-v2-sd');
    var hdUrl = v.getAttribute('data-v2-hd');
    var conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection || {};
    if (conn.saveData === true) return sdUrl || hdUrl;
    if (/(^|-)[23]g$/.test(conn.effectiveType || '')) return sdUrl || hdUrl;
    if (window.innerWidth <= 900) return sdUrl || hdUrl;
    var cssWidth = v.getBoundingClientRect().width || 0;
    var devicePx = cssWidth * (window.devicePixelRatio || 1);
    return (devicePx > 900 && hdUrl) ? hdUrl : (sdUrl || hdUrl);
  }

  function ensureLoaded(v) {
    if (!v || v.dataset.v2Loaded === '1') return;
    var url = pickSource(v);
    if (!url) return;
    v.dataset.v2Loaded = '1';
    v.src = url;
    v.load();
  }

  if (lazyVideos.length && typeof IntersectionObserver !== 'undefined') {
    // Tighter lookahead on mobile — less speculative downloading over cellular.
    var lookahead = window.innerWidth <= 900 ? '150px 0px' : '400px 0px';
    var vizObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var v = entry.target;
        if (entry.isIntersecting) {
          ensureLoaded(v);
          v.play && v.play().catch(function () {});
        } else {
          v.pause && v.pause();
        }
      });
    }, { rootMargin: lookahead, threshold: 0 });

    lazyVideos.forEach(function (v) { vizObserver.observe(v); });
  } else {
    // No IntersectionObserver: load once at the smallest rendition available.
    lazyVideos.forEach(function (v) {
      ensureLoaded(v);
      v.play && v.play().catch(function () {});
    });
  }

  // ─────────────────────────────────────────────
  // 2b. Inject speaker-icon indicator into every wall cell.
  //     Visible only when its video has audio playing.
  // ─────────────────────────────────────────────
  var SPEAKER_SVG =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
      '<path d="M11 5L6 9H2v6h4l5 4V5z"/>' +
      '<path d="M15.54 8.46a5 5 0 0 1 0 7.07"/>' +
      '<path d="M19.07 4.93a10 10 0 0 1 0 14.14"/>' +
    '</svg>';
  document.querySelectorAll('.v2-wall__item').forEach(function (item) {
    if (item.querySelector('.v2-wall__audio-btn')) return;
    var btn = document.createElement('span');
    btn.className = 'v2-wall__audio-btn';
    btn.setAttribute('aria-hidden', 'true');
    btn.innerHTML = SPEAKER_SVG;
    item.appendChild(btn);
  });

  function setAudibleV2(v, on) {
    var item = v.closest('.v2-wall__item');
    if (item) item.classList.toggle('is-audible', !!on);
  }

  // ─────────────────────────────────────────────
  // 3. Hover-to-unmute (desktop) — one video's audio at a time
  // ─────────────────────────────────────────────
  var hasHover = window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  var hasTouch = window.matchMedia && window.matchMedia('(hover: none)').matches;

  if (hasHover) {
    var activeHover = null;
    document.querySelectorAll('.v2-wall__item').forEach(function (item) {
      var v = item.querySelector('.v2-wall__video');
      if (!v) return;
      item.addEventListener('mouseenter', function () {
        if (activeHover && activeHover !== v) { activeHover.muted = true; setAudibleV2(activeHover, false); }
        v.muted = false;
        setAudibleV2(v, true);
        activeHover = v;
        ensureLoaded(v);
        v.play && v.play().catch(function () {});
      });
      item.addEventListener('mouseleave', function () {
        v.muted = true;
        setAudibleV2(v, false);
        if (activeHover === v) { activeHover = null; }
      });
      // Click while hovering → force mute (user override)
      item.addEventListener('click', function () {
        v.muted = true;
        setAudibleV2(v, false);
        if (activeHover === v) { activeHover = null; }
      });
    });
  }

  // ─────────────────────────────────────────────
  // 4. Tap-to-toggle-mute (mobile) — one at a time
  // ─────────────────────────────────────────────
  if (hasTouch) {
    var activeTap = null;
    document.querySelectorAll('.v2-wall__item').forEach(function (item) {
      var v = item.querySelector('.v2-wall__video');
      if (!v) return;
      item.addEventListener('click', function () {
        if (activeTap === v) {
          v.muted = true;
          setAudibleV2(v, false);
          activeTap = null;
        } else {
          if (activeTap) { activeTap.muted = true; setAudibleV2(activeTap, false); }
          v.muted = false;
          setAudibleV2(v, true);
          activeTap = v;
          ensureLoaded(v);
          v.play && v.play().catch(function () {});
        }
      });
    });
  }

}());
